/**
 * The contracts section: two routes and the tab over them.
 *
 * This file used to be the whole section - 462 lines holding a table, a detail
 * screen with an edit form, a create dialog and a hand-rolled animated tab bar,
 * which is well past the point where anybody can find the piece they came for
 * (AGENTS.md §4). Everything now lives in a file named after what it does; the
 * entry point kept its name so no caller had to move (`CrmLayout` looks the
 * section up by this import).
 *
 * - `ContractsList`      the register
 * - `ContractDetail`     one contract, its signature and the form that edits it
 * - `ContractRowMenu`    the actions at the end of a row
 * - `NewContractModal`   the create dialog
 * - `TemplatesManager`   the other tab
 * - `lib.ts` / `status.ts`  the localised-field pick and the tones
 */
import { useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import { PageHead, PillTabs } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ContractDetail } from "./ContractDetail";
import { ContractsList } from "./ContractsList";
import { TemplatesManager } from "./TemplatesManager";

type TabKey = "contracts" | "templates";

/**
 * Contracts and the templates they are cut from, under one head.
 *
 * The tab bar used to be a hand-rolled framer-motion pill with its own layout
 * animation, sitting above a page whose other tab rows are `PillTabs`. It is
 * `PillTabs` now: the sliding highlight was the only thing it bought, and it
 * bought it at the price of this screen's tabs behaving unlike every other tab
 * in the application.
 */
function ContractsHome() {
  const t = useT();
  const [tab, setTab] = useState<TabKey>("contracts");

  return (
    // One open menu at a time across both tabs.
    <PopHost>
      <PageHead
        title={t("contracts.title")}
        sub={t("contracts.intro")}
        actions={
          <PillTabs
            value={tab}
            onChange={setTab}
            tabs={[
              { value: "contracts" as const, label: t("contracts.tabContracts") },
              { value: "templates" as const, label: t("contracts.tabTemplates") },
            ]}
          />
        }
      />

      {tab === "contracts" ? <ContractsList /> : <TemplatesManager />}
    </PopHost>
  );
}

export function ContractsSection({ user: _user }: { user: StaffUser }) {
  return (
    <Routes>
      <Route path="/" element={<ContractsHome />} />
      <Route path="/:id" element={<ContractDetail />} />
    </Routes>
  );
}
